/**
 * Platform-wide number-format defaults - module-level singleton.
 *
 * 0.38.16 - Baseline for the expression-engine cascade:
 *
 *   expression `format(x, "...")` in the form's expression
 *     > per-form settings (page.settings.number.default_format)
 *     > platform defaults (this module)
 *     > raw String() fallback
 *
 * Hydrated once at App mount from /api/public/settings.
 * Consumers read via getNumberFormatDefaults() synchronously at
 * render time; defaults ('' / '') kick in before the fetch resolves
 * so first render isn't blocked and no formatting is applied while
 * the network is in flight.
 *
 * Why module singleton not React context: PageContentRenderer's
 * resolveAdminText is called from many places, sometimes deep
 * inside memoized trees. Threading a context provider every step
 * of the way would be invasive; a synchronous module read is
 * zero-cost and matches how dropdownThresholds and system time
 * format are already handled.
 */

let _defaultFormat = '';
let _defaultLocale = '';
/* 0.42.21 - Group only from this many integer digits; 0 means always.
   A four-digit year or form number reads worse with a separator: 2026
   is a year, "2,026" looks like a quantity. */
let _groupFromDigits = 0;

// 0.38.17 - Listener registry so React components that consume
// getNumberFormatDefaults() can re-render when the platform values
// change (typically on App mount when /api/public/settings resolves).
// Without this, a component that mounted BEFORE the fetch landed
// would capture the initial empty defaults and never notice the
// real values, because setNumberFormatDefaults writes to module
// state (not React state) and thus can't trigger a re-render on
// its own.
const _listeners = new Set();

/* 0.49.06 - The last values the server gave, kept in this browser, so
   the first render already formats numbers the platform's way instead
   of changing them a moment later (5,000 then 5000). The server's
   answer still replaces them. `_known` is false only on a first visit
   before that answer: nothing is formatted by a guess then. */
const _CACHE_KEY = 'gt18.numberFormat';
let _known = false;
try {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(_CACHE_KEY) : null;
  if (raw) {
    const c = JSON.parse(raw);
    if (c && typeof c === 'object') {
      _defaultFormat = typeof c.f === 'string' ? c.f : '';
      _defaultLocale = typeof c.l === 'string' ? c.l : '';
      _groupFromDigits = Number(c.g) || 0;
      _known = true;
    }
  }
} catch (_e) { /* no storage: wait for the server */ }

export function numberFormatKnown() { return _known; }

export function setNumberFormatDefaults({ defaultFormat, defaultLocale, groupFromDigits }) {
  let changed = false;
  if (typeof defaultFormat === 'string' && defaultFormat !== _defaultFormat) {
    _defaultFormat = defaultFormat;
    changed = true;
  }
  if (groupFromDigits !== undefined && Number(groupFromDigits) !== _groupFromDigits) {
    _groupFromDigits = Number(groupFromDigits) || 0;
    changed = true;
  }
  if (typeof defaultLocale === 'string' && defaultLocale !== _defaultLocale) {
    _defaultLocale = defaultLocale;
    changed = true;
  }
  if (!_known) { _known = true; changed = true; }
  try {
    localStorage.setItem(_CACHE_KEY, JSON.stringify({ f: _defaultFormat, l: _defaultLocale, g: _groupFromDigits }));
  } catch (_e) { /* storage full or blocked */ }
  if (changed) {
    for (const fn of _listeners) {
      try { fn(); } catch (_e) { /* ignore individual listener errors */ }
    }
  }
}

export function getNumberFormatDefaults() {
  return {
    defaultFormat: _defaultFormat,
    defaultLocale: _defaultLocale,
    groupFromDigits: _groupFromDigits,
  };
}

// 0.38.17 - Register `fn` to be called whenever the platform
// defaults change. Returns an unsubscribe function suitable for
// use as a useEffect cleanup. Fires zero or one time per
// setNumberFormatDefaults call (only when the value actually
// differs from what's already stored).
export function subscribeToNumberFormatDefaults(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}
